import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { groupByKey, calculateTextingStatus } from '../../../utils/texting';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import usePermissionsHook from '../../../components/ElementWithPermissions/usePermissionsHook';
import { useFetchConversations } from '../../../hooks/data-fetching/useFetchConversations';
import useHasPermission from '../../../hooks/useHasPermission';
import ApplicationService from '../../../services/applicationService';
import ProspectProfileService from '../../../services/prospectProfileService';
import { PROPERTY_PATH_MAP } from './ApplicationsTable_V2.constants';
import {
  buildHeaders,
  buildRows,
  downloadCSV,
  extractLeadSourcesString,
  extractUniqueLeadSources,
} from './ApplicationsTable_V2.utils';
import useUniqueTableName from '../../../hooks/useUniqueTableName';
import useNewColumnsVisibleByDefault from '../../../hooks/useNewColumnsVisibleByDefault';
import useActionsFirstColumnOrder from '../../../hooks/useActionsFirstColumnOrder';
import { useAppFlags } from '../../../utils/featureFlags';
import {
  formatADA,
  formatAccessibilityTypes,
  ADA_COLUMN_IDS,
  shouldForceADAColumnOrder,
} from '../../../utils/ada-helpers';
import { adaptHeaders } from 'utils/table-helpers';

const INITIAL_STATUSES = [
  '2e8c42a3-86dc-4123-87cb-fc6bf930c1e9',
  '353807d7-11cb-43c0-874e-6a5cdcbcf812',
  'a5325dae-8c6c-490b-84e9-fa966e46de99',
  'e31de468-3a4b-42c1-8c76-02a321fe07c6',
];

const useFetchApplications = ({
  organizationId,
  propertyId,
  pageNumber = 1,
  limit = 5,
  filterState,
  enabled,
}) => {
  const applicationService = useMemo(() => new ApplicationService(), []);

  const queryKey = [
    'applications',
    organizationId,
    propertyId,
    pageNumber,
    limit,
    filterState,
    enabled,
  ];
  const { data, isLoading, isSuccess } = useQuery(
    queryKey,
    () =>
      applicationService.getAllApplicationsPaginate(
        organizationId,
        propertyId,
        pageNumber,
        limit,
        null,
        filterState,
        undefined,
      ),
    {
      enabled: Boolean(enabled && organizationId && propertyId),
      refetchOnWindowFocus: false,
    },
  );
  const applications = useMemo(() => data?.results ?? [], [data?.results]);

  return {
    applications,
    isLoading,
    isSuccess,
  };
};

const useHydratedNextActivities = ({
  mappedApplications,
  organizationId,
  propertyId,
}) => {
  const prospectProfileService = useMemo(
    () => new ProspectProfileService(),
    [],
  );
  const [nextActivityByProspectId, setNextActivityByProspectId] = useState({});
  const [isNextActivityColumnLoading, setIsNextActivityColumnLoading] =
    useState(false);
  const [nextActivityColumnError, setNextActivityColumnError] = useState(false);

  const displayedProspectIds = useMemo(
    () =>
      Array.from(
        new Set(
          mappedApplications
            .map(({ prospect, prospectId }) => prospect?.id || prospectId)
            .filter(Boolean),
        ),
      ).sort(),
    [mappedApplications],
  );

  const displayedProspectIdsKey = useMemo(
    () => displayedProspectIds.join(','),
    [displayedProspectIds],
  );

  useEffect(() => {
    const prospectIds = displayedProspectIdsKey
      ? displayedProspectIdsKey.split(',')
      : [];

    if (!organizationId || !propertyId) return;

    if (!prospectIds.length) {
      setIsNextActivityColumnLoading(false);
      setNextActivityColumnError(false);
      return;
    }

    let shouldIgnoreResponse = false;
    setIsNextActivityColumnLoading(true);
    setNextActivityColumnError(false);

    prospectProfileService
      .getNextActivitiesByProspectIds(organizationId, propertyId, prospectIds)
      .then((response) => {
        if (shouldIgnoreResponse) return;

        const nextActivitiesResponse = response?.results ?? [];
        const nextActivitiesByProspectId = nextActivitiesResponse.reduce(
          (acc, { prospectId, nextAct }) => {
            if (prospectId) {
              acc[prospectId] = nextAct ?? null;
            }

            return acc;
          },
          {},
        );

        setNextActivityByProspectId((currentState) => ({
          ...currentState,
          ...nextActivitiesByProspectId,
        }));
      })
      .catch(() => {
        if (shouldIgnoreResponse) return;
        setNextActivityColumnError(true);
      })
      .finally(() => {
        if (shouldIgnoreResponse) return;
        setIsNextActivityColumnLoading(false);
      });

    return () => {
      shouldIgnoreResponse = true;
    };
  }, [
    displayedProspectIdsKey,
    organizationId,
    propertyId,
    prospectProfileService,
  ]);

  const hydratedMappedApplications = useMemo(
    () =>
      mappedApplications?.map((application) => ({
        ...application,
        prospect: {
          ...application.prospect,
          nextAct: Object.prototype.hasOwnProperty.call(
            nextActivityByProspectId,
            application?.prospect?.id,
          )
            ? nextActivityByProspectId[application?.prospect?.id]
            : application?.prospect?.nextAct,
        },
        nextActivityLoading:
          isNextActivityColumnLoading &&
          !Object.prototype.hasOwnProperty.call(
            nextActivityByProspectId,
            application?.prospect?.id,
          ),
        nextActivityError:
          nextActivityColumnError &&
          !Object.prototype.hasOwnProperty.call(
            nextActivityByProspectId,
            application?.prospect?.id,
          ),
      })) || [],
    [
      isNextActivityColumnLoading,
      mappedApplications,
      nextActivityByProspectId,
      nextActivityColumnError,
    ],
  );

  return {
    displayedProspectIds,
    hydratedMappedApplications,
  };
};

export const useManageApplications = ({
  applicationStatuses,
  organizationId,
  propertyId,
  affordablePrograms,
  locale,
  intl,
  onEditActivity,
  onRecordActivity,
  onScheduleActivity,
  onCreateActivity,
  permissions,
  selectedProperty,
  showLeadSourcesColumn,
}) => {
  const [headers, setHeaders] = useState([]);
  const hasFirstMount = useRef(false);
  const name = useUniqueTableName('manage-applications-table');
  const flags = useAppFlags();

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
    initialOrderBy: 'applicationCode',
  });

  useEffect(() => {
    // set initial statuses after options load
    if (applicationStatuses?.length) {
      if (!hasFirstMount.current && !Object.keys(filterState ?? {}).length) {
        handleFilterChange('applicationStatusId', INITIAL_STATUSES);
      }
      hasFirstMount.current = true;
    }
  }, [applicationStatuses, handleFilterChange, filterState]);

  const { backendFilterState } = useMemo(() => {
    const removeValues = (filters, allowFilters = []) => {
      const parsedFilters = {};
      Object.keys(filters).forEach((filterKey) => {
        if (allowFilters.some((filterToAllow) => filterToAllow === filterKey)) {
          parsedFilters[filterKey] = filters[filterKey];
        }
      });

      return parsedFilters;
    };
    return {
      backendFilterState: removeValues(filterState, [
        'applicationStatusId',
        'unit',
      ]),
    };
  }, [filterState]);

  const { applications, isLoading, isSuccess } = useFetchApplications({
    organizationId,
    propertyId,
    orderBy,
    order,
    filterState: backendFilterState,
    enabled: true,
  });

  const twoWayRecipientIds = useMemo(
    () => applications.map((r) => r.twoWayRecipientIds).flat(),
    [applications],
  );

  const hasCommunicationCreatePermission = useHasPermission(
    'communication-create',
  );
  const showTextingColumn =
    selectedProperty.isTwoWayCommunicationActive &&
    hasCommunicationCreatePermission;

  const [conversations] = useFetchConversations(
    twoWayRecipientIds,
    showTextingColumn,
  );
  const conversationsDictionary = useMemo(() => {
    return groupByKey(conversations, (c) => c.recipient.customerId);
  }, [conversations]);

  const results = useMemo(
    () =>
      applications.map((application) => {
        application.texting = calculateTextingStatus(
          application.twoWayRecipientIds,
          conversationsDictionary,
          flags.leslie,
        );
        application.unitNumber =
          application?.unitNumber !== '---' ? application?.unitNumber : null;

        // Display values the ADA columns sort, filter and export on
        application.ada = formatADA(application.isADA);
        application.accessibilityType = formatAccessibilityTypes(
          application.isADA,
          application.accessibilityTypes,
        );

        return application;
      }),
    [applications, conversationsDictionary, flags.leslie],
  );

  const { applicationStatusOptions, assignedToOptions } = useGetFilterOptions({
    applicationStatuses,
    locale,
    results,
  });

  useManageApplicationsHeaders({
    intl,
    affordablePrograms,
    applicationStatusOptions,
    assignedToOptions,
    setHeaders,
    showTextingColumn,
    showLeadSourcesColumn,
    showADAColumns: flags.adaInfo,
    results,
    flags,
  });

  const propertyPathMap = useMemo(() => PROPERTY_PATH_MAP(locale), [locale]);

  // Enrich applications with leadSources data for sorting/filtering (only when flag is enabled)
  const applicationsWithLeadSources = useMemo(() => {
    return (
      results?.map((application) => ({
        ...application,
        leadSources:
          showLeadSourcesColumn && extractLeadSourcesString(application),
      })) || []
    );
  }, [results, showLeadSourcesColumn]);

  const sortedAndFilteredApplications = useTableFilterSortData({
    results: applicationsWithLeadSources,
    order,
    orderBy,
    filterTypeState,
    filterState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP: propertyPathMap,
  });

  const { hydratedMappedApplications, displayedProspectIds } =
    useHydratedNextActivities({
      mappedApplications: sortedAndFilteredApplications,
      organizationId,
      propertyId,
    });

  const rows = useManageApplicationsRows({
    affordablePrograms,
    mappedApplications: hydratedMappedApplications,
    locale,
    intl,
    onEditActivity,
    onRecordActivity,
    onScheduleActivity,
    onCreateActivity,
  });

  // Both memos work together to handle the lifecycle of adding new columns
  // while maintaining a stable drag-and-drop experience

  // Create initial column order to prevent drag-and-drop snap back issue when flags are toggled on/off
  const initialColumnsOrder = useMemo(() => {
    const columnOrder = {};
    headers.forEach((header, index) => {
      columnOrder[header.id] = index;
    });
    return columnOrder;
  }, [headers]);

  // Check if localStorage has the new columns, if not force overwrite. This will prevent ordering conflicts.
  const shouldForceOverwrite = useMemo(() => {
    if (
      shouldForceADAColumnOrder({
        tableName: name,
        showADAColumns: flags.adaInfo,
      })
    ) {
      return true;
    }

    try {
      const storedOrder = localStorage.getItem(`${name}_column-order`);
      if (!storedOrder) return true;

      if (flags.viewMultipleLeadSourcesApplicants) {
        const parsedOrder = JSON.parse(storedOrder);
        const newColumnIds = ['leadSources'];

        // If any new column is missing from stored order, force overwrite
        return newColumnIds.some(
          (columnId) => parsedOrder[columnId] === undefined,
        );
      }

      return false;
    } catch {
      return true;
    }
  }, [name, flags.adaInfo, flags.viewMultipleLeadSourcesApplicants]);

  const {
    initialColumnsOrder: actionsFirstColumnsOrder,
    shouldForceOverwrite: actionsFirstForceOverwrite,
  } = useActionsFirstColumnOrder({
    name,
    headers,
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  // A saved column visibility that predates a column keeps it hidden; show it
  // once, without undoing the columns the user has hidden since.
  const {
    initialColumnsOrder: columnsOrder,
    initialSelectedColumns,
    shouldForceOverwrite: forceOverwrite,
  } = useNewColumnsVisibleByDefault({
    name,
    headers,
    introducedColumnIds: ADA_COLUMN_IDS,
    initialColumnsOrder: actionsFirstColumnsOrder,
    forceOverwrite: actionsFirstForceOverwrite,
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder: columnsOrder,
    initialSelectedColumns,
    forceOverwrite,
  });

  const { CSVIsExporting, handleCSVDownload } =
    useManageApplicationsCSVDownload({
      affordablePrograms,
      filterState,
      locale,
      permissions,
      searchState,
      mappedApplications: hydratedMappedApplications,
      headers,
      showTextingColumn,
      showLeadSourcesColumn,
      showADAColumns: flags.adaInfo,
    });

  return {
    CSVIsExporting,
    allColumnsHidden,
    columnOptions,
    totalCount: results?.length ?? 0,
    count: rows?.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleCSVDownload,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    headers,
    intl,
    isLoading,
    isSuccess,
    name,
    order,
    orderBy,
    displayedProspectIds,
    rows,
    searchState,
    selectedColumns,
  };
};

const useGetFilterOptions = ({ applicationStatuses, locale, results }) => {
  return useMemo(() => {
    const options = {
      applicationStatusOptions: [],
      assignedToOptions: [],
    };

    options.applicationStatusOptions = applicationStatuses.map(
      ({ id, name, translations }) => ({
        value: id,
        text: translations[locale] ?? name,
      }),
    );

    if (results?.length) {
      results.forEach(({ assigneeName }) => {
        if (assigneeName?.length) options.assignedToOptions.push(assigneeName);
      });
      options.assignedToOptions = Array.from(new Set(options.assignedToOptions))
        .sort()
        .map((assigneeName) => ({
          text: assigneeName,
          value: assigneeName?.toLowerCase(),
        }));
    }

    return options;
  }, [applicationStatuses, locale, results]);
};

const useManageApplicationsCSVDownload = ({
  affordablePrograms,
  filterState,
  locale,
  permissions,
  searchState,
  mappedApplications,
  headers,
  showTextingColumn,
  showLeadSourcesColumn,
  showADAColumns,
}) => {
  const [CSVIsExporting, setCSVIsExporting] = useState(false);

  useEffect(() => {
    if (
      CSVIsExporting &&
      Array.isArray(headers) &&
      Array.isArray(mappedApplications)
    ) {
      downloadCSV({
        mappedApplications,
        permissions,
        locale,
        affordablePrograms,
        headers,
        hasAnyFilters:
          Object.keys(filterState ?? {})?.length ||
          Object.keys(searchState ?? {})?.length,
        showTextingColumn,
        showLeadSourcesColumn,
        showADAColumns,
      });
      setCSVIsExporting(false);
    }
  }, [
    CSVIsExporting,
    affordablePrograms,
    mappedApplications,
    filterState,
    locale,
    permissions,
    searchState,
    headers,
    showTextingColumn,
    showLeadSourcesColumn,
    showADAColumns,
  ]);

  return {
    CSVIsExporting,
    handleCSVDownload: () => setCSVIsExporting(true),
  };
};

export const useManageApplicationsHeaders = ({
  intl,
  affordablePrograms,
  applicationStatusOptions,
  assignedToOptions,
  setHeaders,
  showTextingColumn,
  showLeadSourcesColumn,
  showADAColumns,
  results,
  flags,
}) => {
  const { hudProgram, generalAffordableProgram } = affordablePrograms;

  // Extract unique lead sources for filter options
  const leadSourceOptions = useMemo(() => {
    if (!results) return [];

    const uniqueLeadSources = Array.from(extractUniqueLeadSources(results))
      .sort()
      .map((leadSource) => ({
        text: leadSource,
        value: leadSource,
      }));

    return uniqueLeadSources;
  }, [results]);

  useEffect(() => {
    const headers = buildHeaders({
      intl,
      applicationStatusOptions,
      generalAffordableProgram,
      assignedToOptions,
      hudProgram,
      showTextingColumn,
      showLeadSourcesColumn,
      leadSourceOptions,
      showADAColumns,
    });

    adaptHeaders(headers, { flags });

    setHeaders(headers);
  }, [
    applicationStatusOptions,
    assignedToOptions,
    generalAffordableProgram,
    hudProgram,
    intl,
    setHeaders,
    showTextingColumn,
    showLeadSourcesColumn,
    leadSourceOptions,
    showADAColumns,
    flags.leslie,
  ]);
};
const useManageApplicationsRows = ({
  affordablePrograms,
  mappedApplications,
  locale,
  intl,
  onEditActivity,
  onRecordActivity,
  onScheduleActivity,
  onCreateActivity,
}) => {
  const hasActivityReadUpdateDeletePermission = usePermissionsHook({
    scope: ['activity-read', 'activity-update', 'activity-delete'],
  });
  const hasActivityCreatePermission = usePermissionsHook({
    scope: ['activity-create'],
  });
  const { hudProgram, generalAffordableProgram } = affordablePrograms;

  return useMemo(
    () =>
      mappedApplications.length
        ? buildRows({
            mappedApplications,
            generalAffordableProgram,
            hudProgram,
            locale,
            onCreateActivity,
            onEditActivity,
            onRecordActivity,
            onScheduleActivity,
            hasActivityReadUpdateDeletePermission,
            hasActivityCreatePermission,
            intl,
          })
        : [],
    [
      intl,
      hasActivityCreatePermission,
      hasActivityReadUpdateDeletePermission,
      mappedApplications,
      generalAffordableProgram,
      hudProgram,
      locale,
      onCreateActivity,
      onEditActivity,
      onRecordActivity,
      onScheduleActivity,
    ],
  );
};
